import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { HStack, Button, Select, Skeleton, useToast, VStack } from "@chakra-ui/react";
import { PageHeader, SectionCard, EmptyState } from "../../../shared/ui";
import { FileText, Download, Send } from "../../../shared/icons";
import { useVinculosQuery } from "../../vinculos/hooks/useVinculosQuery";
import { relatoriosApi } from "../api/relatoriosApi";
import { trackEvent } from "../../../shared/lib/analytics";
import { adicionarMeses, formatarMesAno } from "../../../shared/lib/date";
import ComprovacaoDocument from "../components/ComprovacaoDocument";
import PeriodShiftsStatusCard from "../components/PeriodShiftsStatusCard";

const MESES_SELECIONAVEIS = 12;

// Mês da comprovação: ?ano=&mes= (o "Cobrar" de um plantão atrasado manda o mês dele) ou,
// sem parâmetro válido, o mês corrente.
function periodoInicial(searchParams, hoje) {
  const ano = Number(searchParams.get("ano"));
  const mes = Number(searchParams.get("mes"));
  if (Number.isInteger(ano) && ano > 2000 && Number.isInteger(mes) && mes >= 1 && mes <= 12) {
    return { ano, mes };
  }
  return { ano: hoje.getFullYear(), mes: hoje.getMonth() + 1 };
}

// Últimos 12 meses (o corrente primeiro), mais o mês pedido na URL se ele estiver fora
// dessa janela.
function opcoesDePeriodo(hoje, selecionado) {
  const opcoes = Array.from({ length: MESES_SELECIONAVEIS }, (_, i) =>
    adicionarMeses(hoje.getFullYear(), hoje.getMonth() + 1, -i)
  );
  const presente = opcoes.some((p) => p.ano === selecionado.ano && p.mes === selecionado.mes);
  return presente ? opcoes : [selecionado, ...opcoes];
}

const chavePeriodo = ({ ano, mes }) => `${ano}-${mes}`;

export default function RelatoriosPage() {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const { data: vinculos = [] } = useVinculosQuery();

  // ?vinculo= (e ?ano=&mes=, no "Cobrar" de Recebíveis) vem dos CTAs "Gerar comprovação"/"Cobrar" espalhados pelo app.
  const [vinculoId, setVinculoId] = useState(searchParams.get("vinculo") ?? "");
  const [comprovacaoId, setComprovacaoId] = useState(null);

  const hoje = new Date();
  const [periodo, setPeriodo] = useState(() => periodoInicial(searchParams, hoje));
  const periodos = opcoesDePeriodo(hoje, periodo);

  const gerarMutation = useMutation({
    mutationFn: relatoriosApi.gerar,
    onSuccess: (resumo) => {
      trackEvent("comprovacao_gerada");
      setComprovacaoId(resumo.id);
    },
    onError: (error) =>
      toast({
        title: "Não foi possível gerar a comprovação",
        description: error.response?.data?.mensagem,
        status: "error",
        duration: 5000,
        isClosable: true,
      }),
  });

  const documentoQuery = useQuery({
    queryKey: ["relatorios", "comprovacao", comprovacaoId],
    queryFn: () => relatoriosApi.obterDocumento(comprovacaoId),
    enabled: Boolean(comprovacaoId),
  });

  const enviarMutation = useMutation({
    mutationFn: relatoriosApi.enviar,
    onSuccess: () => {
      trackEvent("comprovacao_enviada");
      toast({ title: "Comprovação enviada ao RH do tomador", status: "success", duration: 4000 });
    },
    onError: (error) =>
      toast({
        title: "Não foi possível enviar",
        description: error.response?.data?.mensagem,
        status: "error",
        duration: 5000,
        isClosable: true,
      }),
  });

  const gerar = () => {
    if (!vinculoId) return;
    gerarMutation.mutate({ vinculoId, ano: periodo.ano, mes: periodo.mes });
  };

  const trocarVinculo = () => {
    setComprovacaoId(null);
    setVinculoId("");
  };

  return (
    <>
      <PageHeader
        title="Comprovação para cobrança"
        description="Gere um demonstrativo formal dos plantões para enviar ao tomador"
        actions={
          comprovacaoId ? (
            <HStack spacing={2} flexWrap="wrap">
              <Button variant="outline" onClick={trocarVinculo}>
                Outro vínculo
              </Button>
              <Button variant="outline" leftIcon={<Download size={16} />} onClick={() => relatoriosApi.baixarPDF(comprovacaoId)}>
                Baixar PDF
              </Button>
              <Button
                variant="secondary"
                leftIcon={<Send size={16} />}
                isLoading={enviarMutation.isPending}
                onClick={() => enviarMutation.mutate({ id: comprovacaoId })}
              >
                Enviar ao RH
              </Button>
            </HStack>
          ) : null
        }
      />

      {!comprovacaoId ? (
        <SectionCard>
          <EmptyState
            icon={FileText}
            title="Escolha o vínculo para gerar a comprovação"
            description="O demonstrativo reúne os plantões do mês escolhido e fica congelado como registro formal."
            action={
              <VStack spacing={3} w="100%" maxW={{ base: "100%", sm: "280px" }} minW={{ sm: "280px" }}>
                <Select placeholder="Escolha o vínculo" value={vinculoId} onChange={(e) => setVinculoId(e.target.value)}>
                  {vinculos.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.nome}
                    </option>
                  ))}
                </Select>
                <Select
                  aria-label="Mês dos plantões"
                  value={chavePeriodo(periodo)}
                  onChange={(e) => {
                    const [ano, mes] = e.target.value.split("-").map(Number);
                    setPeriodo({ ano, mes });
                  }}
                >
                  {periodos.map((p) => (
                    <option key={chavePeriodo(p)} value={chavePeriodo(p)}>
                      {formatarMesAno(p.ano, p.mes)}
                    </option>
                  ))}
                </Select>
                <Button w="100%" isDisabled={!vinculoId} isLoading={gerarMutation.isPending} onClick={gerar}>
                  Gerar comprovação
                </Button>
              </VStack>
            }
          />
        </SectionCard>
      ) : documentoQuery.isLoading || !documentoQuery.data ? (
        <Skeleton height="500px" borderRadius="xl" />
      ) : (
        <VStack align="stretch" spacing={5}>
          <PeriodShiftsStatusCard
            vinculoId={vinculoId}
            vinculoNome={vinculos.find((v) => String(v.id) === String(vinculoId))?.nome}
            ano={periodo.ano}
            mes={periodo.mes}
          />
          <ComprovacaoDocument documento={documentoQuery.data} />
        </VStack>
      )}
    </>
  );
}
